<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />

        <b-card v-else>
            <!-- Header -->
            <h5 class="mb-0">
                <i class="fa fa-fw fa-rotate-left mr-2" />Refund Konser
            </h5>
            <hr />

            <!-- Filter Section -->
            <b-row>
                <b-col md="4">
                    <b-form-group label="Customer">
                        <multiselect
                            v-model="filter.customer"
                            label="name"
                            track-by="id"
                            :internal-search="true"
                            deselect-label=""
                            select-label=""
                            placeholder="Semua customer"
                            :options="optCustomer"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="Konser">
                        <multiselect
                            v-model="filter.concert"
                            label="name"
                            track-by="id"
                            :internal-search="true"
                            deselect-label=""
                            select-label=""
                            placeholder="Semua konser"
                            :options="optConcerts"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="Status">
                        <b-form-select
                            v-model="filter.status"
                            :options="optStatus"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col class="text-right">
                    <b-button
                        variant="primary"
                        class="mr-2"
                        @click="getItems()"
                    >
                        <i class="fa fa-search mr-2" />Cari
                    </b-button>
                    <b-button variant="danger" @click="resetFilter">
                        Reset
                    </b-button>
                </b-col>
            </b-row>

            <!-- Table -->
            <b-table
                bordered
                hover
                show-empty
                no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                responsive
                class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data refund.
                </template>

                <template v-slot:cell(customer)="row">
                    <div>
                        <span class="font-weight-bold">{{
                            row.item.customer.name
                        }}</span>
                        <span
                            class="d-block small text-muted"
                            v-if="row.item.customer.phone"
                        >
                            {{ row.item.customer.phone }}
                        </span>
                        <span
                            class="d-block small"
                            v-if="
                                row.item.down_payment &&
                                    row.item.down_payment.invoice_number
                            "
                        >
                            {{ row.item.down_payment.invoice_number }}
                        </span>
                    </div>
                </template>

                <template v-slot:cell(concert)="row">
                    <div>
                        <span class="font-weight-bold">{{
                            row.item.concert.name
                        }}</span>
                        <span
                            class="d-block small text-muted"
                            v-if="row.item.concert.date"
                        >
                            {{ $helper.parseDate(row.item.concert.date) }}
                        </span>
                        <span
                            class="d-block small text-danger"
                            v-if="row.item.source === REFUND_SOURCE.MANUAL_CANCEL"
                        >
                            Dibatalkan: {{ row.item.cancelled_by || "-" }}
                        </span>
                        <span
                            class="d-block small text-danger"
                            v-else-if="
                                row.item.blacklist &&
                                    row.item.blacklist.reason_label
                            "
                        >
                            Skorsing: {{ row.item.blacklist.reason_label }}
                        </span>
                    </div>
                </template>

                <template v-slot:cell(amount)="row">
                    <span>
                        Rp.
                        {{ $helper.formatThousand(Math.abs(row.item.amount)) }}
                    </span>
                </template>

                <template v-slot:cell(status)="row">
                    <b-badge
                        :variant="getRefundStatusBadge(row.item.status)[0]"
                    >
                        {{ getRefundStatusBadge(row.item.status)[1] }}
                    </b-badge>
                </template>

                <template v-slot:cell(confirmed_by)="row">
                    <div v-if="row.item.confirmed_by">
                        {{ row.item.confirmed_by }}
                        <span
                            class="d-block small text-muted"
                            v-if="row.item.confirmed_at"
                        >
                            {{ $helper.parseDate(row.item.confirmed_at) }},
                            {{ $helper.parseHourMinute(row.item.confirmed_at) }}
                        </span>
                    </div>
                    <span v-else class="text-muted">-</span>
                    <span
                        class="d-block small text-muted"
                        v-if="row.item.notes"
                    >
                        Ket: {{ row.item.notes }}
                    </span>
                </template>

                <template v-slot:cell(created_at)="row">
                    <span v-if="row.item.created_at">
                        {{ $helper.parseDate(row.item.created_at) }},
                        {{ $helper.parseHourMinute(row.item.created_at) }}
                    </span>
                    <span v-else>-</span>
                </template>

                <template v-slot:cell(action)="row">
                    <div
                        class="d-flex justify-content-center align-items-center"
                    >
                        <b-button
                            v-if="row.item.proof_url"
                            size="sm"
                            variant="info"
                            class="text-white mr-1"
                            v-b-tooltip.hover.top
                            title="Lihat Bukti Transfer"
                            @click="openProof(row.item.proof_url)"
                        >
                            <i class="fa fa-fw fa-image" />
                        </b-button>

                        <b-button
                            v-if="row.item.status === REFUND_STATUS.PENDING"
                            size="sm"
                            variant="success"
                            v-b-tooltip.hover.top
                            title="Konfirmasi Refund"
                            @click="prepareConfirm(row.item)"
                        >
                            <i class="fa fa-fw fa-check" />
                        </b-button>
                    </div>
                </template>
            </b-table>

            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>

        <ModalKonfirmasiRefund
            ref="modalKonfirmasi"
            @refresh="getItems(currentPage)"
        />
    </div>
</template>

<script>
import ModalKonfirmasiRefund from "./ModalKonfirmasiRefund.vue";
import { REFUND_STATUS, REFUND_SOURCE, getRefundStatusBadge } from "../konserHelper";

export default {
    name: "ListRefundKonser",

    components: {
        ModalKonfirmasiRefund
    },
    data() {
        return {
            initStatus: 0,
            isBusy: true,

            currentPage: 1,
            perPage: 10,
            totalRows: 0,

            filter: {
                status: -1,
                customer: null,
                concert: null
            },

            options: [
                { value: 10, text: "10" },
                { value: 20, text: "20" },
                { value: 30, text: "30" },
                { value: 40, text: "40" },
                { value: 50, text: "50" }
            ],

            optStatus: [
                { value: -1, text: "Semua Status" },
                { value: REFUND_STATUS.PENDING, text: "Menunggu Refund" },
                { value: REFUND_STATUS.CONFIRMED, text: "Refund Selesai" }
            ],

            optConcerts: [],
            optCustomer: [],

            REFUND_STATUS,
            REFUND_SOURCE,

            items: [],

            fields: [
                { key: "customer", label: "Customer", sortable: false },
                { key: "concert", label: "Konser", sortable: false },
                {
                    key: "amount",
                    label: "Jumlah Refund",
                    sortable: false
                },
                {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "created_at",
                    label: "Dibuat",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "confirmed_by",
                    label: "Dikonfirmasi Oleh",
                    sortable: false
                },
                {
                    key: "action",
                    label: "Aksi",
                    thClass: "text-center",
                    tdClass: "text-center"
                }
            ]
        };
    },

    methods: {
        init() {
            this.initStatus = 0;

            Promise.all([
                this.getItems(),
                this.getConcerts(),
                this.getCustomers()
            ])
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(() => {
                    this.initStatus = -1;
                });
        },

        getItems(page = 1) {
            this.isBusy = true;
            this.items = [];

            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            let param = this.$helper.getBasePagedQuery(
                this.currentPage,
                this.perPage
            );

            if (this.filter.status !== -1) {
                param += "&status=" + this.filter.status;
            }

            if (this.filter.customer !== null) {
                param += "&customer_id=" + this.filter.customer.id;
            }

            if (this.filter.concert !== null) {
                param += "&concert_id=" + this.filter.concert.id;
            }

            return this.$axios
                .get("concerts/refunds" + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        getConcerts() {
            return this.$axios
                .get("concerts/selection")
                .then(res => {
                    this.optConcerts = (res.data.data || []).map(item => {
                        return {
                            id: item.id,
                            name: item.date
                                ? `${item.name} (${this.$helper.parseDate(
                                      item.date
                                  )})`
                                : item.name
                        };
                    });
                })
                .catch(() => {
                    this.optConcerts = [];
                });
        },

        getCustomers() {
            return this.$axios
                .get("customer/selection")
                .then(res => {
                    this.optCustomer = res.data.data;
                })
                .catch(() => {
                    this.optCustomer = [];
                });
        },

        resetFilter() {
            this.filter = {
                status: -1,
                customer: null,
                concert: null
            };
            this.getItems();
        },

        prepareConfirm(item) {
            this.$refs.modalKonfirmasi.openModal(item);
        },

        openProof(url) {
            window.open(url, "_blank");
        },

        getRefundStatusBadge,

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getItems(this.currentPage);
        }
    },

    created() {
        this.init();
    }
};
</script>
